import React from "react";
import {Routes, Route, Navigate} from "react-router-dom";
import {useAuth} from "@/context/AuthContext";
import {UsersProvider} from "@/context/UsersContext";
import ClientPage from "../pages/PeopleList/ClientPage";
import AddPersonPage from "../pages/AddPerson/AddPersonPage";
import EmployeesPage from "../pages/Employees";
import DetailInfoPage from "../pages/DetailInfo/DetailInfoPage";
import CalculatorPage from "../pages/Calculator/CalculatorPage";
import Settings from "../pages/Settings";
import Analytics from "../pages/Analytics";
import ContractPrint from "../pages/ContractPrint/ContractPrint";
import NoAccessPage from "../pages/AccessPage";
import {Layout} from "@/components/layout/Layout";
import Dashboard from "@/pages/DashboardPage";

const rolePermissions: Record<"admin" | "superadmin" | "user", string[]> = {
    admin: [
        "/people",
        "/add-person",
        "/employees",
        "/detailInfo",
        "/calculator",
        "/settings",
        "/analytics",
        "/print",
        "/dashboard",
    ],
    superadmin: [
        "/people",
        "/add-person",
        "/employees",
        "/detailInfo",
        "/calculator",
        "/settings",
        "/analytics",
        "/print",
        "/dashboard",
    ],
    user: ["/settings"]
};

const PrivateRoute: React.FC = () => {
    const {user, role} = useAuth();

    if (!user) return <Navigate to="/signin" replace/>;

    if (role === "user") {
        return <NoAccessPage/>;
    }

    const safeRole =
        role && Object.keys(rolePermissions).includes(role)
            ? (role as keyof typeof rolePermissions)
            : null;

    const canAccess = (route: string) =>
        safeRole && rolePermissions[safeRole]?.includes(route);

    return (
        <UsersProvider>
            <div>
                <div>
                    <Routes>
                        <Route element={<Layout/>}>
                            <Route path="/" element={<Navigate to="/people" replace/>}/>
                            <Route path={"/dashboard"}
                                   element={canAccess("/dashboard") ? <Dashboard/> :
                                       <Navigate to="/settings" replace/>}/>

                            <Route
                                path="/people"
                                element={canAccess("/people") ? <ClientPage/> : <Navigate to="/settings" replace/>}
                            />
                            <Route
                                path="/print/:id"
                                element={canAccess("/print") ? <ContractPrint/> : <Navigate to="/settings" replace/>}
                            />
                            <Route
                                path="/add-person"
                                element={
                                    canAccess("/add-person") ? <AddPersonPage/> : <Navigate to="/settings" replace/>
                                }
                            />
                            <Route
                                path="/employees"
                                element={
                                    canAccess("/employees") ? <EmployeesPage/> : <Navigate to="/settings" replace/>
                                }
                            />
                            <Route
                                path="/detailInfo/:id"
                                element={
                                    canAccess("/detailInfo") ? <DetailInfoPage/> : <Navigate to="/settings" replace/>
                                }
                            />
                            <Route
                                path="/calculator"
                                element={
                                    canAccess("/calculator") ? <CalculatorPage/> : <Navigate to="/settings" replace/>
                                }
                            />
                            <Route path="/settings" element={<Settings/>}/>
                            <Route
                                path="/analytics"
                                element={canAccess("/analytics") ? <Analytics/> : <Navigate to="/settings" replace/>}
                            />

                            <Route path="*" element={<Navigate to="/settings" replace/>}/>
                        </Route>
                    </Routes>
                </div>
            </div>
        </UsersProvider>
    );
};

export default PrivateRoute;
